import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { collection, doc, getFirestore, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import axios from "axios";

// Realtime half of the support console. The REST API stays the write path and
// the initial load; Firestore pushes new tickets and replies as they land. If
// the Firebase web config is missing (or sign-in fails) the page falls back to
// refetching over REST — see isChatRealtimeAvailable().

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// The API owns the root collection (live-viarento in production, test-viarento
// everywhere else) and hands it over with the session, so a production build of
// this console can be pointed at a staging API without rebuilding.
let chatRoot = null;

export const setChatRoot = (root) => {
    if (typeof root === "string" && root) chatRoot = root;
};

const ROOT_DOCUMENT = "support_chat";

// The support console can be opened before anything else has read the session,
// so fall back to asking for it rather than failing the subscription.
const ensureChatRoot = async () => {
    if (chatRoot) return chatRoot;
    const response = await axios.get("/api/admin/auth/me");
    setChatRoot(response.data?.chat_root);
    if (!chatRoot) throw new Error("This session did not carry a support chat root.");
    return chatRoot;
};

// The console never shows more than the most recent slice of the queue live;
// older tickets are reached through the REST list with its filters.
const ROOM_LISTEN_LIMIT = 100;

export const isChatRealtimeAvailable = () =>
    Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);

const getFirebaseApp = () => (getApps().length ? getApp() : initializeApp(firebaseConfig));

let authPromise = null;

export const ensureChatAuth = () => {
    if (!isChatRealtimeAvailable()) return Promise.reject(new Error("Firebase web config missing"));
    if (authPromise) return authPromise;

    authPromise = (async () => {
        const [response] = await Promise.all([
            axios.get("/api/admin/support-chat/token"),
            ensureChatRoot(),
        ]);
        const auth = getAuth(getFirebaseApp());
        await signInWithCustomToken(auth, response.data.token);
        return { userId: response.data.user_id, role: response.data.role };
    })().catch((error) => {
        authPromise = null;
        throw error;
    });

    return authPromise;
};

// Every subscription is set up after ensureChatAuth() has resolved, so the root
// is known by the time this runs.
const roomsCollection = () => {
    if (!chatRoot) throw new Error("Support chat root unknown. Call ensureChatAuth() first.");
    return collection(getFirestore(getFirebaseApp()), chatRoot, ROOT_DOCUMENT, "chat_rooms");
};

const messagesCollection = (roomId) => collection(doc(roomsCollection(), String(roomId)), "messages");

// Admins read every ticket, so this query carries no filters — the security
// rules admit it on the strength of the admin claim alone.
export const subscribeToAllRooms = ({ onChange, onError }) =>
    onSnapshot(
        query(roomsCollection(), orderBy("latest_created_at", "desc"), limit(ROOM_LISTEN_LIMIT)),
        (snapshot) => onChange(snapshot.docs.map((d) => d.data())),
        onError
    );

export const subscribeToRoomMessages = ({ roomId, onChange, onError }) =>
    onSnapshot(
        query(messagesCollection(roomId), orderBy("id", "asc")),
        (snapshot) => onChange(snapshot.docs.map((d) => d.data())),
        onError
    );
